import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import PieChart from "../../../../components/Charts/PieChart";
import { GLOBAL_REPORT_PLATFORM_COLORS } from "../../../GlobalReport/utils/globalReportChartPalette";
import { useTranslationApp } from "../../../../lib/i18next";
import { OWNER_AD_SPEND_PLATFORMS_TITLE } from "../../../../i18n/keysTranslations";
import { BORDER_RADIUS } from "../../../../utils/constants";
import { logoPlatform } from "../../../../utils/string";
import AdSpendTrendChip from "./AdSpendTrendChip";
import {
  AD_SPEND_CARD_SX,
  AD_SPEND_CHART_TOOLTIP_SX,
  formatAdSpendPlatform,
  formatAdSpendUsd,
} from "./adSpendDisplay";

const AdSpendPlatformBreakdown = ({
  platformShare = [],
  totalSpendUSD = 0,
  showTrend = false,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslationApp();
  const language = i18n.language;

  const chartConfig = {};
  platformShare.forEach((row) => {
    chartConfig[row.platform] = {
      label: formatAdSpendPlatform({ platform: row.platform, t }),
      color: GLOBAL_REPORT_PLATFORM_COLORS[row.platform],
      valueFormatter: (value) => formatAdSpendUsd({ value, language }),
    };
  });

  const chartData = platformShare.map((row) => ({
    platform: row.platform,
    spendUSD: row.spendUSD,
  }));

  return (
    <Box
      sx={{
        ...AD_SPEND_CARD_SX,
        borderRadius: BORDER_RADIUS,
        p: 2.5,
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 1,
          flexWrap: "wrap",
          mb: 1.5,
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {t(OWNER_AD_SPEND_PLATFORMS_TITLE)}
        </Typography>
        {isLoading ? null : (
          <Typography variant="body2" color="text.secondary">
            {formatAdSpendUsd({ value: totalSpendUSD, language })}
          </Typography>
        )}
      </Box>
      {isLoading ? (
        <Skeleton variant="rounded" height={280} />
      ) : (
        <>
          {chartData.length > 0 ? (
            <PieChart
              embedded
              showTotal={false}
              showLabels={false}
              hasCartesianGrid={false}
              chartData={chartData}
              chartConfig={chartConfig}
              labelKey="platform"
              valueKey="spendUSD"
              sx={AD_SPEND_CHART_TOOLTIP_SX}
            />
          ) : null}
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}
          >
            {platformShare.map((row) => (
              <Box key={row.platform}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      component="img"
                      src={logoPlatform(row.platform)}
                      alt=""
                      sx={{ width: 16, height: 16, objectFit: "contain" }}
                    />
                    <Typography variant="body2">
                      {formatAdSpendPlatform({ platform: row.platform, t })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatAdSpendUsd({ value: row.spendUSD, language })}
                    </Typography>
                    {showTrend ? <AdSpendTrendChip trend={row.trend} /> : null}
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(row.sharePercent, 100)}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "#f3f4f6",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor:
                        GLOBAL_REPORT_PLATFORM_COLORS[row.platform],
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default AdSpendPlatformBreakdown;
